import { toast } from "react-hot-toast";

import { Action } from "../actions/actionDefinitions";
import { ImageData, Option } from "../types";
import { trackEvent } from "../utils/tracking";

const API_ENDPOINT =
  "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent";
const MAX_RETRIES = 3;

interface TextPart {
  text: string;
}

interface InlineDataPart {
  inline_data: {
    mime_type: string;
    data: string;
  };
}

type Part = TextPart | InlineDataPart;

interface Schema {
  type: string;
  format?: string;
  description?: string;
  nullable?: boolean;
  enum?: string[];
  maxItems?: string;
  minItems?: string;
  properties?: Record<string, Schema>;
  required?: string[];
  propertyOrdering?: string[];
  items?: Schema;
}

export const translatePrompt = async (prompt: string, apiKey: string) => {
  let retryCount = 0;
  let lastError = null;

  while (retryCount < 3) {
    try {
      const payload = {
        contents: [
          {
            parts: [
              {
                text:
                  "Translate this prompt to English, only return the translated prompt, no other text or preamble. If the prompt is already in English, just return the prompt. Here is the prompt: " +
                  prompt,
              },
            ],
          },
        ],
        generationConfig: {
          responseModalities: ["TEXT"],
          responseMimeType: "application/json",
          responseSchema: {
            type: "OBJECT",
            properties: {
              translatedPrompt: { type: "STRING" },
            },
          },
        },
      };
      const response = await fetch(`${API_ENDPOINT}?key=${apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      // Handle rate limit errors with sleep and retry
      if (response.status === 429) {
        const shouldRetry = await handleRateLimitError();
        if (shouldRetry) {
          // Don't increment retry count for rate limit errors
          continue;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return JSON.parse(data.candidates[0].content.parts[0].text).translatedPrompt;
    } catch (error) {
      lastError = error;
      retryCount++;
      await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
    }
  }
  throw lastError;
};

// Update the rate limit handler to sleep and return a retry flag
const handleRateLimitError = async (): Promise<boolean> => {
  const waitTime = 60; // Default 60 seconds
  // Show a toast notification about the rate limit
  toast.loading(`Rate limit exceeded. Waiting ${waitTime} seconds before retrying...`, {
    duration: waitTime * 1000,
  });
  // Sleep for the wait time
  await new Promise((resolve) => setTimeout(resolve, waitTime * 1000));

  // Return true to indicate we should retry
  return true;
};

// Update generateImageFromText to handle rate limits with sleep
export const generateImageFromText = async (
  translatedPrompt: string,
  apiKey: string
): Promise<string> => {
  // Create the payload for text-to-image generation
  const payload = {
    contents: [{ parts: [{ text: translatedPrompt }] }],
    generationConfig: { responseModalities: ["TEXT", "IMAGE"] },
    safetySettings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_CIVIC_INTEGRITY",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_NONE",
      },
    ],
  };

  let lastError = null;
  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      const response = await fetch(`${API_ENDPOINT}?key=${apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        const shouldRetry = await handleRateLimitError();
        if (shouldRetry) {
          continue;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for IMAGE_SAFETY finish reason
      if (data.candidates?.[0]?.finishReason === "IMAGE_SAFETY") {
        throw new Error(
          "The request was rejected due to content safety concerns. Please try a different prompt."
        );
      }

      // Extract the image data from the response
      for (const part of data.candidates[0].content.parts) {
        if (part.inlineData) {
          return part.inlineData.data;
        }
        if (part.text) {
          throw new Error(part.text);
        }
      }
      throw new Error("No image was generated");
    } catch (error) {
      lastError = error;
      if (i === MAX_RETRIES - 1) {
        throw error;
      }

      // Exponential backoff for other errors
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw lastError;
};

export const transformImages = async (
  apiKey: string,
  firstImage: ImageData,
  secondImage: ImageData | null,
  prompt: string,
  actionId?: string
): Promise<string> => {
  // Track the event if actionId is provided
  if (actionId) {
    trackEvent(`thumbnail_reviewer_${actionId}_api_call`);
  }

  const parts: Part[] = [
    {
      inline_data: {
        mime_type: firstImage.mimeType,
        data: firstImage.base64,
      },
    },
  ];
  if (secondImage) {
    parts.push({
      inline_data: {
        mime_type: secondImage.mimeType,
        data: secondImage.base64,
      },
    });
  }
  parts.push({
    text: prompt,
  });
  const payload = {
    contents: [{ parts }],
    generationConfig: { temperature: 1.0, responseModalities: ["TEXT", "IMAGE"] },
    safetySettings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_CIVIC_INTEGRITY",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_NONE",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_NONE",
      },
    ],
  };

  let lastError = null;
  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      const response = await fetch(`${API_ENDPOINT}?key=${apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        const shouldRetry = await handleRateLimitError();
        if (shouldRetry) {
          continue;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for IMAGE_SAFETY finish reason
      if (data.candidates?.[0]?.finishReason === "IMAGE_SAFETY") {
        throw new Error(
          "The request was rejected due to content safety concerns. Please try different images."
        );
      }

      for (const part of data.candidates[0].content.parts) {
        if (part.inlineData) {
          return part.inlineData.data;
        }
        if (part.text) {
          throw new Error(part.text);
        }
      }
      throw new Error("No valid response received");
    } catch (error) {
      lastError = error;
      if (i === MAX_RETRIES - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw lastError;
};

// Main function to process any action
export const processAction = async (
  apiKey: string,
  action: Action,
  firstImage: ImageData,
  secondImage: ImageData | null = null,
  selectedOption?: Option
): Promise<string> => {
  // Track the event
  const trackingData: { action: string; option?: string } = {
    action: action.id,
  };
  if (selectedOption) {
    trackingData.option = selectedOption.id;
  }
  trackEvent(`thumbnail_reviewer_api_call`, trackingData);

  // Call the transformImages function with the generated prompt
  return transformImages(apiKey, firstImage, secondImage, action.prompt(selectedOption), action.id);
};

export const analyzeImageWithPrompt = async (
  prompt: string,
  imageData: ImageData,
  apiKey: string
): Promise<string> => {
  if (!apiKey) {
    throw new Error("API key is missing");
  }

  // Log the API endpoint being used
  console.log("Using API endpoint:", API_ENDPOINT);

  const payload = {
    contents: [
      {
        parts: [
          {
            inline_data: {
              mime_type: imageData.mimeType,
              data: imageData.base64,
            },
          },
          {
            text: prompt,
          },
        ],
      },
    ],
    generationConfig: {
      temperature: 0.2,
      maxOutputTokens: 3200,
      responseModalities: ["TEXT"],
    },
  };

  try {
    const response = await fetch(`${API_ENDPOINT}?key=${apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    // Log response status
    console.log("Response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.candidates[0].content.parts[0].text.trim();
  } catch (error) {
    console.error("API call error:", error);
    throw error;
  }
};

export const analyzeImageWithStructuredOutput = async <T>(
  prompt: string,
  imageData: ImageData | undefined,
  apiKey: string,
  responseSchema: Schema
): Promise<T> => {
  if (!apiKey) {
    throw new Error("API key is missing");
  }
  let parts = [{ text: prompt }] as any[];
  if (imageData) {
    parts = [
      {
        inline_data: {
          mime_type: imageData.mimeType,
          data: imageData.base64,
        },
      },
      ...parts,
    ];
  }
  const payload = {
    contents: [
      {
        parts: parts,
      },
    ],
    generationConfig: {
      temperature: 0.2,
      maxOutputTokens: 1600,
      responseModalities: ["TEXT"],
      responseMimeType: "application/json",
      responseSchema: responseSchema,
    },
  };

  let lastError = null;
  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      const response = await fetch(`${API_ENDPOINT}?key=${apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        const shouldRetry = await handleRateLimitError();
        if (shouldRetry) {
          continue;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return JSON.parse(data.candidates[0].content.parts[0].text) as T;
    } catch (error) {
      lastError = error;
      if (i === MAX_RETRIES - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw lastError;
};
